import React, { useState, useEffect } from "react";
import { Button, Col, Container, Row, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "state/hooks";
import { getUserState } from "state/slices/user-slice";
import { getGroupState } from "state/slices/group-slice";
import store from "state/store";
import { useAuth } from "hooks/auth";
import { getConsoleMessages, clearConsoleMessages } from "state/slices/console-slice";
import { getNetworkRequests, clearNetworkRequests } from "state/slices/network-slice";

import DebugSection from "./components/DebugSection";
import BuildInfo from "./components/BuildInfo";
import UserGroupInfo from "./components/UserGroupInfo";
import ConsoleLogs from "./components/ConsoleLogs";
import NetworkRequests from "./components/NetworkRequests";
import LocalStorageManager from "./components/LocalStorageManager";
import StorageViewer from "./components/StorageViewer";
import AppConfigManager from "./components/AppConfigManager";
import GroupSwitcher from "./components/GroupSwitcher";

const getAllCookies = (): Record<string, string> => {
  const cookies = document.cookie.split("; ");
  const cookieObj: Record<string, string> = {};
  cookies.forEach((cookie) => {
    const [key, value] = cookie.split("=");
    if (key && value) {
      cookieObj[key] = decodeURIComponent(value);
    }
  });
  return cookieObj;
};

const getEnvironmentInfo = () => {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    windowSize: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timestamp: new Date().toISOString(),
  };
};

const getSessionStorageData = () => {
  const data: Record<string, string | null> = {};
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key) {
      data[key] = sessionStorage.getItem(key);
    }
  }
  return data;
};

const DebugPage: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const user = useAppSelector(getUserState);
  const group = useAppSelector(getGroupState);
  const dispatch = useAppDispatch();

  const [sessionStorageData, setSessionStorageData] = useState(getSessionStorageData());
  const [autoScrollLogs, setAutoScrollLogs] = useState(true);
  const [reduxCopied, setReduxCopied] = useState(false);

  const consoleLogs = useAppSelector(getConsoleMessages);
  const networkRequests = useAppSelector(getNetworkRequests);

  const handleClearConsoleLogs = () => {
    dispatch(clearConsoleMessages());
  };

  const handleClearNetworkRequests = () => {
    dispatch(clearNetworkRequests());
  };

  const reduxState = store.getState();
  const environmentInfo = getEnvironmentInfo();
  const cookieData = getAllCookies();
  const commitHash = process.env.REACT_APP_COMMIT_HASH || "Not available";
  const nodeEnv = process.env.NODE_ENV || "Not available";
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "Not available";

  const handleClearAllStorage = () => {
    if (window.confirm("Are you sure you want to clear all localStorage and sessionStorage?")) {
      localStorage.clear();
      sessionStorage.clear();
      setSessionStorageData({});
    }
  };

  const handleExportDebugInfo = () => {
    const debugInfo = {
      timestamp: new Date().toISOString(),
      commitHash,
      nodeEnv,
      backendUrl,
      currentUrl: window.location.href,
      user: {
        id: user.id,
        super_admin: user.super_admin,
        authSignedIn: auth.signedIn,
      },
      group: {
        id: group.id,
        name: group.name,
      },
      environment: environmentInfo,
      localStorage: Object.fromEntries(Object.keys(localStorage).map(key => [key, localStorage.getItem(key)])),
      sessionStorage: sessionStorageData,
      cookies: cookieData,
      consoleLogs: consoleLogs.map(log => ({
        timestamp: log.timestamp,
        type: log.type,
        message: log.message,
        stack: log.stack,
      })),
      networkRequests: networkRequests.map(request => ({
        timestamp: request.timestamp,
        url: request.url,
        method: request.method,
        status: request.status,
        duration: request.duration,
        error: request.error,
        responseBody: request.responseBody,
        requestHeaders: request.requestHeaders,
      })),
      reduxState,
    };

    const blob = new Blob([JSON.stringify(debugInfo, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sparket-debug-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setSessionStorageData(getSessionStorageData());
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Container
      fluid
      className="p-4"
      style={{
        backgroundColor: "white",
        maxWidth: "100vw",
        overflowX: "hidden",
        margin: "0 auto",
        width: "100%",
        minHeight: "100vh",
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mt-4 mb-4">🔧 Debug Page</h1>
        <div>
          <Button variant="success" onClick={handleExportDebugInfo} className="me-2">
            Export Debug Info
          </Button>
          <Button variant="secondary" onClick={() => navigate("/")}>
            Go Home
          </Button>
        </div>
      </div>

      <Row className="mb-4">
        <Col md={12}>
          <DebugSection
            title="Build Information"
            icon="🏗️"
            defaultOpen={true}
          >
            <BuildInfo 
              commitHash={commitHash}
              nodeEnv={nodeEnv}
              backendUrl={backendUrl}
            />
          </DebugSection>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <UserGroupInfo 
            user={user}
            group={group}
            authSignedIn={auth.signedIn}
          />
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <DebugSection
            title="Group Switcher"
            icon="🔀"
            defaultOpen={true}
          >
            <GroupSwitcher />
          </DebugSection>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <DebugSection
            title="App Configuration"
            icon="⚙️"
            defaultOpen={true}
          >
            <AppConfigManager />
          </DebugSection>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <DebugSection
            title="Environment Info"
            icon="🌍"
          >
            <Table striped bordered hover responsive size="sm">
              <tbody>
                {Object.entries(environmentInfo).map(([key, value]) => (
                  <tr key={key}>
                    <td style={{ fontWeight: "bold", width: "200px" }}>{key}</td>
                    <td style={{ fontFamily: "monospace", fontSize: "12px" }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </DebugSection>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <DebugSection
            title="Redux State"
            icon="🔄"
          >
            <div className="mb-2">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={async () => {
                  await navigator.clipboard.writeText(JSON.stringify(reduxState, null, 2));
                  setReduxCopied(true);
                  setTimeout(() => setReduxCopied(false), 2000);
                }}
              >
                {reduxCopied ? "Copied!" : "Copy"}
              </Button>
            </div>
            <div style={{
              padding: "15px",
              border: "1px solid #dee2e6",
              borderRadius: "8px",
              backgroundColor: "#f8f9fa",
              maxHeight: "500px",
              overflowY: "scroll",
              fontFamily: "monospace",
              fontSize: "12px",
              whiteSpace: "pre-wrap"
            }}>
              {JSON.stringify(reduxState, null, 2)}
            </div>
          </DebugSection>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <DebugSection
            title="Console Logs"
            icon="📝"
            extraBadges={<span className="badge bg-primary">{consoleLogs.length} logs</span>}
          >
            <ConsoleLogs
              consoleLogs={consoleLogs}
              autoScrollLogs={autoScrollLogs}
              onClearLogs={handleClearConsoleLogs}
              onAutoScrollChange={setAutoScrollLogs}
            />
          </DebugSection>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <DebugSection
            title="Network Requests"
            icon="🌐"
            extraBadges={<span className="badge bg-primary">{networkRequests.length} requests</span>}
          >
            <NetworkRequests
              networkRequests={networkRequests}
              onClearRequests={handleClearNetworkRequests}
            />
          </DebugSection>
        </Col>
      </Row>


      <Row className="mb-4">
        <Col md={12}>
          <DebugSection
            title="Local Storage Management"
            icon="💾"
          >
            <LocalStorageManager onClearAllStorage={handleClearAllStorage} />
          </DebugSection>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <DebugSection
            title="Session Storage"
            icon="📝"
          >
            <StorageViewer data={sessionStorageData} />
          </DebugSection>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <DebugSection
            title="Cookies"
            icon="🍪"
            defaultOpen={true}
          >
            <StorageViewer data={cookieData} />
          </DebugSection>
        </Col>
      </Row>
    </Container>
  );
};

export default DebugPage;